import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { shortTypeLabel } from '../../shared/qbank/type-labels';
import { isWeak } from '../../shared/qbank/weak-types';

import {
  getTypeAccuracy,
  SKILL_PRACTICE_QUERY_KEYS,
  type TypeAccuracyRow,
} from './skill-practice-api';
import s from './WeakTypesCard.module.css';

const MAX_SHOWN = 3;

/**
 * "Sharpen your weak spots."
 *
 * This is the payoff for everything the drill feature records. A student does
 * not arrive at the app knowing they fail Matching Headings four times in ten —
 * the data has always been there, and until now it was thrown away.
 *
 * Renders nothing at all when there isn't enough evidence to be honest about,
 * the same way ContinueCard is invisible when nothing is unfinished. A card
 * that says "no data yet" every day teaches the student to stop reading it.
 */
export function WeakTypesCard(): JSX.Element | null {
  const query = useQuery({
    queryKey: SKILL_PRACTICE_QUERY_KEYS.accuracy(null),
    queryFn: () => getTypeAccuracy(null),
  });

  const weak: TypeAccuracyRow[] = (query.data ?? [])
    .filter((row) => isWeak(row))
    .sort((a, b) => (a.accuracy ?? 100) - (b.accuracy ?? 100))
    .slice(0, MAX_SHOWN);

  if (weak.length === 0) return null;

  return (
    <section className={s.root} aria-labelledby="weak-types-heading">
      <header className={s.head}>
        <h2 className={s.title} id="weak-types-heading">
          Sharpen your weak spots
        </h2>
        <Link className={s.all} to="/student/skill-practice">
          All question types →
        </Link>
      </header>
      <ul className={s.list}>
        {weak.map((row) => (
          <li key={`${row.skill}-${row.type_code}`}>
            <Link className={s.chip} to={`/student/skill-practice/${row.skill}/${row.type_code}`}>
              <span className={s.chipLabel}>{shortTypeLabel(row.type_code)}</span>
              <span className={s.chipMeta}>
                {row.accuracy}% right · {row.answered} answered
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
